import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { registrationAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, EyeSlash, ArrowRight, Sparkle } from '@phosphor-icons/react';

const RegistrationPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', password: '',
    clinic_name: '', phone: '', referral_source: '',
  });

  const passwordStrength = () => {
    const p = form.password;
    if (!p) return { level: 0, label: '', color: '' };
    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    const levels = [
      { level: 1, label: 'Weak', color: 'bg-red-400' },
      { level: 2, label: 'Fair', color: 'bg-amber-400' },
      { level: 3, label: 'Good', color: 'bg-emerald-400' },
      { level: 4, label: 'Strong', color: 'bg-emerald-600' },
    ];
    return levels[Math.min(score, 4) - 1] || levels[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.clinic_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const res = await registrationAPI.registerTenant(form);
      const { token, user, organisation, message } = res.data || {};

      // Enumeration-safe path: backend returned a generic message (email already
      // exists, or other reason it cannot proceed). Show the message and nudge
      // toward login, but do NOT reveal whether the email is taken.
      if (!token) {
        toast.success(message || 'If your email is valid, please check your inbox.');
        navigate('/login');
        return;
      }

      // Auto-login on real success
      localStorage.setItem('cliniqueo_token', token);
      localStorage.setItem('cliniqueo_user', JSON.stringify(user));
      localStorage.setItem('cliniqueo_organisation', JSON.stringify(organisation));
      login(token, user, organisation);
      toast.success('Welcome to Cliniqueo! Let\'s get you set up.');
      navigate('/onboarding');
    } catch (e) {
      // 429 from rate limiter shows generic message (no detail leak)
      if (e.response?.status === 429) {
        toast.error('Too many requests, please try again shortly');
      } else {
        toast.error(e.response?.data?.detail || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const strength = passwordStrength();

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex" data-testid="registration-page">
      {/* Left branding */}
      <div className="hidden lg:flex flex-col justify-center items-center flex-1 px-12">
        <div className="max-w-md">
          <div className="flex items-center gap-2 mb-6">
            <Sparkle size={28} weight="fill" className="text-amber-400" />
            <span className="font-serif text-3xl font-bold text-white tracking-tight">Cliniqueo</span>
          </div>
          <h1 className="font-serif text-4xl font-bold text-white leading-tight mb-4">
            The clinic management platform built for you
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed mb-8">
            Start your 14-day free trial. Full Professional features, no credit card required.
          </p>
          <div className="space-y-3 text-sm text-zinc-400">
            {['Online booking page', 'Appointment diary & CRM', 'Custom forms & consent', 'Payment processing', 'SMS & email reminders'].map(f => (
              <div key={f} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Sparkle size={24} weight="fill" className="text-amber-400" />
            <span className="font-serif text-2xl font-bold text-white">Cliniqueo</span>
          </div>

          <h2 className="font-serif text-2xl font-bold text-white mb-1">Create your clinic</h2>
          <p className="text-zinc-400 text-sm mb-8">14-day free trial. No credit card needed.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-zinc-300 mb-1 block">Your name *</label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Jane Smith"
                className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500"
                data-testid="register-name"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-300 mb-1 block">Email *</label>
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="you@clinic.com"
                className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500"
                data-testid="register-email"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-300 mb-1 block">Password *</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Min 8 characters"
                  className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 pr-10"
                  data-testid="register-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400">
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {form.password && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-zinc-700 overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${strength.color}`} style={{ width: `${strength.level * 25}%` }} />
                  </div>
                  <span className="text-xs text-zinc-400">{strength.label}</span>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm text-zinc-300 mb-1 block">Clinic name *</label>
              <Input
                value={form.clinic_name}
                onChange={e => setForm(f => ({ ...f, clinic_name: e.target.value }))}
                placeholder="The Skin Nurse"
                className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500"
                data-testid="register-clinic-name"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-300 mb-1 block">Phone</label>
              <Input
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+44 7xxx xxxxxx"
                className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500"
                data-testid="register-phone"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-300 mb-1 block">How did you hear about us?</label>
              <Select value={form.referral_source} onValueChange={v => setForm(f => ({ ...f, referral_source: v }))}>
                <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white" data-testid="register-referral">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="google">Google Search</SelectItem>
                  <SelectItem value="social">Social Media</SelectItem>
                  <SelectItem value="referral">Friend / Colleague</SelectItem>
                  <SelectItem value="event">Event / Conference</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="w-full btn-primary h-11 text-sm gap-2" disabled={loading} data-testid="register-submit">
              {loading ? 'Creating your clinic...' : <><span>Start Free Trial</span> <ArrowRight size={16} /></>}
            </Button>
          </form>

          <p className="text-center text-sm text-zinc-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-amber-400 hover:text-amber-300 font-medium" data-testid="register-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
